import { ReactNode } from "react";
import { Modal } from "antd";
import styled from "styled-components";
import { colors } from "../app/styles";

export interface ConfirmRow {
  label: string;
  value: ReactNode;
  /** Render the value in the brand accent (e.g. counted qty / batch). */
  highlight?: boolean;
}

interface ConfirmModalProps {
  open: boolean;
  /** Small glyph shown in the header badge. */
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  rows: ConfirmRow[];
  /** Italic footnote under the details (e.g. what happens on confirm). */
  note?: ReactNode;
  /** Error surfaced from a failed confirm — keeps the modal open. */
  error?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const Head = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const IconBadge = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #e0fbee;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Title = styled.div`
  font-weight: 800;
  font-size: 18px;
  color: ${colors.primaryDark};
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${colors.textMuted};
`;

const RowsCard = styled.div`
  margin-top: 16px;
  border: 1px solid ${colors.cardBorder};
  border-radius: 12px;
  background: #fafcfb;
  padding: 6px 14px;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;

  & + & {
    border-top: 1px solid ${colors.cardBorder};
  }
`;

const RowLabel = styled.span`
  font-size: 13px;
  color: ${colors.textMuted};
  flex: 0 0 auto;
`;

const RowValue = styled.span<{ $highlight?: boolean }>`
  font-size: 15px;
  font-weight: 800;
  text-align: right;
  overflow-wrap: anywhere;
  color: ${(p) => (p.$highlight ? colors.primary : colors.primaryDark)};
`;

const Note = styled.div`
  margin-top: 14px;
  text-align: center;
  font-size: 12px;
  font-style: italic;
  color: ${colors.textMuted};
`;

const ErrorText = styled.div`
  margin-top: 14px;
  border: 1px solid #ffa39e;
  background: #fff1f0;
  border-radius: 10px;
  padding: 10px 12px;
  color: #a8071a;
  font-weight: 600;
  font-size: 13px;
`;

const Footer = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 12px;

  & > button {
    flex: 1 1 0;
    height: 52px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

const CancelButton = styled.button`
  border: 1px solid ${colors.cardBorder};
  background: #ffffff;
  color: ${colors.primaryDark};
`;

const ConfirmButton = styled.button`
  border: none;
  background: ${colors.primaryDark};
  color: #ffffff;
`;

export default function ConfirmModal({
  open,
  icon,
  title,
  subtitle,
  rows,
  note,
  error,
  confirmLabel = "Confirm",
  cancelLabel = "Go Back",
  loading = false,
  onCancel,
  onConfirm,
}: ConfirmModalProps) {
  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      centered
      width={360}
      maskClosable={!loading}
      onCancel={loading ? undefined : onCancel}
      styles={{ body: { padding: 4 } }}
    >
      <Head>
        {icon && <IconBadge>{icon}</IconBadge>}
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
      </Head>

      <RowsCard>
        {rows.map((row) => (
          <Row key={row.label}>
            <RowLabel>{row.label}</RowLabel>
            <RowValue $highlight={row.highlight}>{row.value}</RowValue>
          </Row>
        ))}
      </RowsCard>

      {note && !error && <Note>{note}</Note>}
      {error && <ErrorText>{error}</ErrorText>}

      <Footer>
        {/* Cancel first so the modal's initial focus lands here — a stray wedge
            Enter dismisses the review rather than committing it. */}
        <CancelButton type="button" onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </CancelButton>
        <ConfirmButton type="button" onClick={onConfirm} disabled={loading}>
          {loading ? "Working…" : confirmLabel}
        </ConfirmButton>
      </Footer>
    </Modal>
  );
}
